<script setup lang="ts">
/**
 * Aura Share Certificate (ordinary shares) — CAU008 · Studio panel. 증권 1부(수동 입력) + create task.
 *   회사 선택 시 회사명·등록번호·등록사무소 주소 자동채움.
 *   ✨ 주주 선택 시 그 주주 증서의 증권번호·주식 수·발행일 자동(주식수 → 영문 표기 자동).
 *   서명: 증서 서명은 2명(고객 회신 2026-10-27) — [[Director_Signature]] = 이사 1명,
 *   [[Director_Secretary_Signature]] = 이사 또는 비서 1명. 서명자 JSON(백엔드 서명 블록), 자동으로 넣지 않고 운영자가 고른다.
 *   자체완결 cdd(useCddFill) — AGM 모듈과 같은 패턴.
 */
import { sgAppliedAddressLine } from '@/features/startTask/preset_task/sg/preset/utils/sgAppliedAddressLine';
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, bareKey, typeOf } from '../cdd/useCddFill';
import ShareCertDoc from '../docs/ShareCertDoc.vue';
import { normalizeShareCertForWord, numberToWords } from '../utils/normalizeShareCertForWord';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { getShareCertificateSnapshot } from '@/features/startTask/api/shareCertificateApi';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  appliedAddress?: {
    key: string; line?: string; line1?: string; line2?: string; line3?: string;
    town?: string; state?: string; postcode?: string; country?: string; seq?: number;
  } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const TASK_CODE = 'CAU008';
const DOC_TYPE = 'SG_AURA_SHARE_CERT';

const router = useRouter();
const fill = provideAgmFill();
const autofilledKeys = ref<Set<string>>(new Set());
const lastAutofillCd = ref('');
// Edit 재진입: 저장값 복원 후엔 회사 자동채움(비동기, 복원보다 늦게 끝남)이 직접 고친 값·서명자를 덮지 않게(§L-1).
const restoredFromSaved = ref(false);
const taskConfigSteps = ref<string[]>([]);
/** 회사 주주 목록(agm-source members — 주식자본 클래스별 행은 주주당 1행으로 합산됨) */
const shareMembers = ref<any[]>([]);
/** 주주 선택으로 마지막에 자동 입력한 값(증권번호·주식 수·영문·발행일) — 주주·회사를 바꿀 때 이 값만 비운다 */
const lastAuto: Record<string, string> = {};

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

// ✨ 조회 역할 — 주주 칸은 그 회사의 SHAREHOLDER, 서명 첫 칸은 DIRECTOR, 둘째 칸은 SECRETARY
//   (조회 창에서 역할을 바꿔 이사도 고를 수 있다), 그 외 사람 칸은 CONTACT.
function lookupRoleFor(key: string): { role: string; scoped: boolean } {
  const b = bareKey(key);
  if (b === 'shareholder_name') return { role: 'SHAREHOLDER', scoped: true };
  if (b === 'director_name') return { role: 'DIRECTOR', scoped: true };
  if (b === 'second_signer_name') return { role: 'SECRETARY', scoped: true };
  return { role: 'CONTACT', scoped: false };
}

/** 고른 사람의 직책 — 조회 결과의 직위/역할 글자에서 Director / Secretary 판정(없으면 '') */
function designationOf(p: Record<string, any>): string {
  const t = [p?.positionName, p?.position, p?.title, p?.role, p?.positionGroup].map((v) => String(v ?? '')).join(' ');
  if (/secretary/i.test(t)) return 'Secretary';
  if (/director/i.test(t)) return 'Director';
  return '';
}
fill.setActivateHandler((key: string) => {
  const t = typeOf(key);
  if (t === 'entity') { emit('change-entity'); return; }
  if (t === 'person') {
    const { role, scoped } = lookupRoleFor(key);
    emit('request-person-lookup', {
      fieldGroup: 'aura-share-cert',
      key: bareKey(key),
      scopedKey: key,
      defaultRole: role,
      lookupType: 'officer',
      entityCd: scoped ? entityCd.value : '',
    });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });

/** 회사 선택 → 회사명·등록번호·등록사무소 주소. */
async function autofillFromEntity(entityCdRaw: string) {
  const cd = String(entityCdRaw || '').trim();
  if (!cd || cd === lastAutofillCd.value) return;
  const entityChanged = !!lastAutofillCd.value && lastAutofillCd.value !== cd;
  lastAutofillCd.value = cd;
  if (entityChanged) {
    // §L-2 회사 변경 — 이전 회사 값을 지우고 새 회사로 다시 채운다.
    restoredFromSaved.value = false;
    for (const k of ['director_name', 'second_signer_name', 'second_signer_designation']) fill.setValue(k, '');
    for (const k of autofilledKeys.value) fill.setValue(k, '');
    // 이전 회사 주주 증서로 자동 입력한 값도 비운다(직접 고친 값은 유지)
    for (const [k, v] of Object.entries(lastAuto)) {
      if (String(fill.values[k] ?? '').trim() === v) fill.setValue(k, '');
      delete lastAuto[k];
    }
    shareMembers.value = [];
  }
  const written = new Set<string>();
  const set = (k: string, v: unknown) => {
    const s = String(v ?? '').trim();
    if (restoredFromSaved.value && String(fill.values[k] ?? '').trim()) return; // 저장값 보존
    if (s) { fill.setValue(k, s); written.add(k); }
  };

  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' })
      .then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    shareMembers.value = Array.isArray(src.members) ? src.members : [];
    set('company_name', src.companyName);
    set('company_registration_no', src.registrationNo);
    if (src.registeredOfficeAddress) set('registered_office_address', src.registeredOfficeAddress);
  } catch (e) { console.warn('[ShareCert] agm-source failed', e); }

  // 서명자는 자동으로 넣지 않는다(고객 회신 2026-10-27: 증서 서명은 2명 — 이사 1 + 비서 1 또는 이사 2).
  //   첫 칸 이사 1명, 둘째 칸 이사 또는 비서 1명을 운영자가 ✨ 로 고른다.

  autofilledKeys.value = written;
  emitProgress();
}

watch(() => props.chatFormData, (fd) => {
  if (!fd) return;
  const nm = String(fd.company_name || fd.entityName || '').trim();
  if (nm && !restoredFromSaved.value && fill.values.company_name !== nm) fill.setValue('company_name', nm);
  const cd = String(fd.entityCd || fd.icpCd || '').trim();
  if (cd) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd) void autofillFromEntity(cd); }, { immediate: true });

/** 주주를 고르면 그 주주 증서 값으로 덮어쓴다(이미 입력한 값이 있어도) — 증서에 값이 없는 칸은 그대로. */
function setAuto(key: string, v: unknown) {
  const s = String(v ?? '').trim();
  if (!s) return;
  lastAuto[key] = s;
  fill.setValue(key, s);
}

/**
 * ✨ 주주 선택 → 그 주주의 증서(일반 프리셋 Share Certificate 와 같은 /share-certificate/snapshot — 최신 1장)에서
 *   증권번호·주식 수·발행일을 채운다(영문 표기·본문 숫자는 문서가 주식 수에서 자동 생성).
 *   증서가 없으면 주식 수만 회사 주주 데이터(보통주 합계)로.
 */
async function fillFromHolderCertificate(p: Record<string, any>) {
  const nm = String(p?.name || p?.contactName || p?.fullName || '').trim().toUpperCase();
  const member = shareMembers.value.find((x: any) => nm && String(x?.name || '').trim().toUpperCase() === nm);
  const contactCd = String(p?.contactCd || p?.CONTACT_CD || member?.contactCd || '').trim();
  // 다른 주주로 바꾸면 이전 주주 증서로 자동 입력한 값부터 비운다(새 증서에 없는 칸에 옛 값이 남지 않게)
  for (const [k, v] of Object.entries(lastAuto)) {
    if (String(fill.values[k] ?? '').trim() === v) fill.setValue(k, '');
    delete lastAuto[k];
  }
  let snap: any = null;
  if (entityCd.value && contactCd) {
    try { snap = await getShareCertificateSnapshot(entityCd.value, contactCd); } catch (e) { console.warn('[ShareCert] snapshot failed', e); }
  }
  setAuto('certificate_no', snap?.cert_no);
  const shares = String(snap?.no_of_shares ?? '').replace(/[,\s]/g, '')
    || (Number(member?.sharesOrdinary || 0) > 0 ? String(member.sharesOrdinary) : '');
  setAuto('number_of_shares', shares);
  // 영문 표기도 새 주식 수로 (직접 써 둔 영문이 있어도 덮어쓴다)
  if (shares) setAuto('number_of_shares_in_words', numberToWords(shares));
  setAuto('date_of_issue', snap?.issue_date_iso || '');
  emitProgress();
}

watch(() => props.selectedPerson, (p) => {
  if (!p || !fill.activeKey.value) return;
  const b = bareKey(fill.activeKey.value);
  fill.applyPersonToActive(p as Record<string, any>);
  if (b === 'shareholder_name') void fillFromHolderCertificate(p as Record<string, any>);
  // 서명자 직책 — 첫 칸은 이사, 둘째 칸은 고른 사람 직위(판정 안 되면 운영자가 직접 입력)
  if (b === 'director_name') fill.setValue('director_designation', 'Director');
  if (b === 'second_signer_name') {
    const d = designationOf(p as Record<string, any>);
    if (d) fill.setValue('second_signer_designation', d);
  }
  emitProgress();
});
watch(() => props.selectedAddress, (a) => {
  if (!a?.value || !fill.activeKey.value) return;
  fill.setValue(fill.activeKey.value, a.value);
  emitProgress();
});
watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  const line = sgAppliedAddressLine(a);
  if (line) { fill.setValue(a.key, line); emitProgress(); }
}, { deep: true });

onMounted(async () => {
  if (!String(fill.values.director_designation ?? '').trim()) fill.setValue('director_designation', 'Director');
  emitProgress();
  try {
    const cfg: any = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : [];
  } catch { /* ignore */ }
});

function gatherState() {
  return {
    values: { ...fill.exportPayload() },
    entityCd: entityCd.value,
    taskTitle: effectiveTitle.value,
  };
}
function restoreState(s: any) {
  if (!s) return;
  restoredFromSaved.value = true;
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v));
  }
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || TASK_CODE).trim() || TASK_CODE;
    const values = fill.exportPayload();
    const formData = normalizeShareCertForWord(values);
    // Edit 저장 시 대소문자만 다른 변수 키를 화면 값으로 맞추는 Aura Share Certificate 표시값(PreSetTaskDetailPage)
    formData.aura_share_cert = 'yes';
    // T-Sign 참여자 = 서명 2명(문서 서명 자리는 일반 변수 — 점선 + 이름 + 직책)
    const tsignSigners: SignerEntry[] = [
      { name: formData.director_name, title: formData.director_designation || 'Director' },
      { name: formData.second_signer_name, title: formData.second_signer_designation || '' },
    ].filter((x) => String(x.name || '').trim()).map((x, i) => ({
      id: `aura-sc-${i}`,
      name: String(x.name).trim(),
      title: String(x.title || '').trim(),
      role: /secretary/i.test(String(x.title)) ? 'secretary' : 'director',
      isManual: true,
      showTitle: true,
    } as SignerEntry));

    const officerData = {
      entityCd: entityCd.value || null,
      icpCd: entityCd.value || null,
      presetForm: gatherState(),
      docType: DOC_TYPE,
      isMyPreset: props.selection?.isMyPreset === true, // §L-3 Edit 문서 목록 = 마이프리셋 전체 템플릿
      formData,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'Share Certificate (ordinary shares)',
          taskTitle: effectiveTitle.value,
          taskFlow: {
            createDocument: steps.length === 0 ? true : steps.includes('doc'),
            requestTSign: steps.includes('t-sign'),
            proceedWithEFiling: steps.includes('e-filing'),
          },
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false; return;
      }
    }

    progress.value = 'Preparing documents…';
    // 템플릿 1종(증권). Edit 재진입 시 생성 문서가 섞여 올 수 있어 tmpId 기준 dedupe.
    const seen = new Set<number>();
    const docs = (props.selection.documents || []).filter((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid || seen.has(tid)) return false;
      seen.add(tid);
      return true;
    });
    const holder = String(formData.shareholder_name || '').trim();
    const docsToSave = docs.map((d: any) => {
      const baseLabel = String(d?.templateName || d?.docCode || 'Share Certificate')
        .replace(/\.docx?$/i, '').split(' — ')[0].trim();
      return {
        templateId: Number(d?.tmpId || d?.templateId),
        label: holder ? `${baseLabel} — ${holder}` : baseLabel,
        personName: holder || String(formData.company_name || 'Company'),
        formData,
        signers: tsignSigners.length ? tsignSigners : undefined,
      };
    });

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
      } catch (err: any) {
        progress.value = `⚠ Document save failed: ${err?.message || err}`;
      }
    }
    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="sc-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Share Certificate (ordinary shares)' }}</div>
        <div class="co-subtitle">Aura — share certificate</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field v-model="titleModel" density="compact" variant="outlined" hide-details label="Task Title" />
    </div>

    <div class="co-doc">
      <div class="simple-frame"><ShareCertDoc /></div>
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || creating" @click="createTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.sc-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.sc-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-doc { flex: 1; min-height: 0; }
.simple-frame { width: 794px; max-width: 100%; margin: 0 auto; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20,30,60,0.09); padding: 40px; box-sizing: border-box; font-size: 12px; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
